import { useState, useEffect } from "react";
import { Music2, ExternalLink, Check, X, Trash2, Clock, Eye, Instagram, Youtube } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchSubmissions = async () => {
    const token = localStorage.getItem("token");
    const url = filter === "all" ? `${API}/api/submissions` : `${API}/api/submissions?status=${filter}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setSubmissions(await res.json());
    setLoading(false);
  };

  useEffect(() => { fetchSubmissions(); }, [filter]);

  const updateStatus = async (id, status) => {
    const token = localStorage.getItem("token");
    await fetch(`${API}/api/submissions/${id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    fetchSubmissions();
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm("Remover esta submissão?")) return;
    const token = localStorage.getItem("token");
    await fetch(`${API}/api/submissions/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchSubmissions();
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    reviewed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    approved: "bg-green-500/20 text-green-400 border-green-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  const statusLabels = { pending: "Pendente", reviewed: "Analisado", approved: "Aprovado", rejected: "Rejeitado" };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white" data-testid="submissions-title">Divulgações</h1>
            <p className="text-zinc-400 text-sm">Bandas e artistas que querem divulgar no ArrochaPlay</p>
          </div>
          <div className="flex gap-2">
            {["all", "pending", "approved", "rejected"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-full font-semibold transition-all ${
                  filter === f ? "bg-[#FF007F] text-white" : "bg-white/5 text-zinc-400 hover:bg-white/10"
                }`} data-testid={`filter-${f}`}>
                {f === "all" ? "Todos" : statusLabels[f]}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-zinc-500">Carregando...</div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-20">
            <Music2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500">Nenhuma submissão encontrada</p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map(sub => (
              <div key={sub.id} className="bg-[#111] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-all" data-testid={`submission-${sub.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-white text-lg">{sub.band_name}</h3>
                    <p className="text-zinc-400 text-sm">{sub.name} &middot; {sub.email}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs rounded-full border font-semibold ${statusColors[sub.status] || statusColors.pending}`}>
                    {statusLabels[sub.status] || sub.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                  {sub.whatsapp && <div className="text-zinc-400"><span className="text-zinc-600">WhatsApp:</span> {sub.whatsapp}</div>}
                  {sub.city && <div className="text-zinc-400"><span className="text-zinc-600">Cidade:</span> {sub.city}-{sub.state}</div>}
                  <div className="text-zinc-400"><span className="text-zinc-600">Gênero:</span> {sub.genre}</div>
                  <div className="text-zinc-400"><span className="text-zinc-600">Tipo:</span> {sub.submission_type}</div>
                </div>

                {/* Links */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {sub.sua_musica_url && (
                    <a href={sub.sua_musica_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 text-green-400 text-xs rounded-full hover:bg-green-500/20 transition-all">
                      <Music2 size={12} /> Sua Música <ExternalLink size={10} />
                    </a>
                  )}
                  {sub.instagram_url && (
                    <a href={sub.instagram_url.startsWith("http") ? sub.instagram_url : `https://instagram.com/${sub.instagram_url.replace("@","")}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-1.5 bg-pink-500/10 text-pink-400 text-xs rounded-full hover:bg-pink-500/20 transition-all">
                      <Instagram size={12} /> {sub.instagram_url} <ExternalLink size={10} />
                    </a>
                  )}
                  {sub.youtube_url && (
                    <a href={sub.youtube_url.startsWith("http") ? sub.youtube_url : `https://${sub.youtube_url}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 text-xs rounded-full hover:bg-red-500/20 transition-all">
                      <Youtube size={12} /> YouTube <ExternalLink size={10} />
                    </a>
                  )}
                  {sub.spotify_url && (
                    <a href={sub.spotify_url.startsWith("http") ? sub.spotify_url : `https://${sub.spotify_url}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 text-green-300 text-xs rounded-full hover:bg-green-500/20 transition-all">
                      <Music2 size={12} /> Spotify <ExternalLink size={10} />
                    </a>
                  )}
                </div>

                {sub.message && (
                  <p className="text-zinc-500 text-sm bg-white/5 rounded-lg p-3 mb-3 italic">"{sub.message}"</p>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="text-zinc-600 text-xs flex items-center gap-1">
                    <Clock size={12} /> {new Date(sub.created_at).toLocaleDateString("pt-BR")}
                  </span>
                  <div className="flex gap-2">
                    {sub.status === "pending" && (
                      <>
                        <button onClick={() => updateStatus(sub.id, "approved")}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 text-green-400 text-xs rounded-lg hover:bg-green-500/20 transition-all"
                          data-testid={`approve-${sub.id}`}>
                          <Check size={14} /> Aprovar
                        </button>
                        <button onClick={() => updateStatus(sub.id, "rejected")}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 text-red-400 text-xs rounded-lg hover:bg-red-500/20 transition-all"
                          data-testid={`reject-${sub.id}`}>
                          <X size={14} /> Rejeitar
                        </button>
                      </>
                    )}
                    <button onClick={() => deleteSubmission(sub.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white/5 text-zinc-500 text-xs rounded-lg hover:bg-red-500/10 hover:text-red-400 transition-all"
                      data-testid={`delete-${sub.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
